import React, { useState, useEffect, useCallback, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { extractApiError } from '../lib/apiErrors';
import { useLocationTracking } from '../lib/useLocationTracking';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { formatSydneyTime } from '../utils/timezone';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ShiftWorkflowModal } from '../components/ShiftWorkflowModal';
import { ShiftSignOffModal } from '../components/ShiftSignOffModal';
import { NewShiftAlert } from '../components/NewShiftAlert';
import WeatherForecastWidget from '../components/WeatherForecastWidget';
import {
  Bell, Calendar, Clock, MapPin, CheckCircle2, AlertTriangle,
  RefreshCw, Shield, Route, Loader2, ChevronRight, Check, X,
  Play, PenLine, Navigation, ClipboardCheck, Battery, FileWarning
} from 'lucide-react';

// ─── CONSTANTS ──────────────────────────────────────────────────────────────

const getShiftDisplayName = (shift, fallback = 'Assigned Site') => {
  if (!shift) return fallback;
  return (
    shift.display_name ||
    shift.deployment_name ||
    shift.patrol_plan_name ||
    shift.deployment_site_name ||
    shift.site_name ||
    fallback
  );
};

const ACTIVE_WORKFLOW_STAGES = ['started', 'patrol_active', 'patrol_plan_loaded', 'patrol_interrupted'];
const URGENCY_THRESHOLDS = { WARNING_MS: 30 * 60 * 1000, CRITICAL_MS: 0 }; // 30min, 0 = past start

// ─── HELPER: Format time/date ───────────────────────────────────────────────
const fmt = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-AU', {
    timeZone: 'Australia/Sydney',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
};

// ─── HOOK: useShiftCountdown ────────────────────────────────────────────────
// Computes a live countdown from server-trusted start_time.
// Returns { text, urgency, diffMs, isOverdue } — ticks every second.
function useShiftCountdown(startTimeISO) {
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!startTimeISO) return { text: '', urgency: 'normal', diffMs: 0, isOverdue: false };

  const startMs = new Date(startTimeISO).getTime();
  const diffMs = startMs - now;
  const isOverdue = diffMs <= 0;
  const absDiff = Math.abs(diffMs);

  // Urgency states
  let urgency = 'normal';
  if (isOverdue) urgency = 'critical';
  else if (diffMs <= URGENCY_THRESHOLDS.WARNING_MS) urgency = 'warning';

  // Build human-readable text
  const hours = Math.floor(absDiff / 3_600_000);
  const mins  = Math.floor((absDiff % 3_600_000) / 60_000);
  const secs  = Math.floor((absDiff % 60_000) / 1000);

  let text;
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remH = hours % 24;
    text = isOverdue ? `Shift started ${days}d ${remH}h ago` : `Shift starts in ${days}d ${remH}h`;
  } else if (hours > 0) {
    text = isOverdue ? `Shift started ${hours}h ${mins}m ago` : `Shift starts in ${hours}h ${mins}m`;
  } else if (mins > 0) {
    text = isOverdue ? `Shift started ${mins}m ago` : `Shift starts in ${mins}m ${secs}s`;
  } else {
    text = isOverdue ? `Shift started ${secs}s ago` : `Shift starts in ${secs}s`;
  }

  return { text, urgency, diffMs, isOverdue };
}

// ═══════════════════════════════════════════════════════════════════════════
// SIGN-ON PRIORITY CARD — Full-screen blocker when sign-on is pending
// ═══════════════════════════════════════════════════════════════════════════
function SignOnPriorityCard({ shift, onContinue, loading }) {
  const stageLabels = { accepted: 'Accept', sop_acknowledged: 'SOP', fit_for_work: 'Fitness', geofence_validated: 'Location', signed_on: 'Sign-On' };
  const stageOrder = ['accepted', 'sop_acknowledged', 'fit_for_work', 'geofence_validated', 'signed_on'];
  const currentIdx = stageOrder.indexOf(shift.workflow_stage);

  // Live countdown from server-trusted start_time
  const countdown = useShiftCountdown(shift.start_time);

  // Urgency-driven colours
  const urgencyStyles = {
    normal:   { bg: 'bg-zinc-800/60', border: 'border-zinc-700', text: 'text-cyan-300',   icon: 'text-cyan-400' },
    warning:  { bg: 'bg-amber-900/30', border: 'border-amber-500/40', text: 'text-amber-300', icon: 'text-amber-400' },
    critical: { bg: 'bg-red-900/30',   border: 'border-red-500/40',   text: 'text-red-300',   icon: 'text-red-400' },
  };
  const uStyle = urgencyStyles[countdown.urgency] || urgencyStyles.normal;


  return (
    <Card className="bg-gradient-to-br from-cyan-900/80 to-blue-900/70 border-2 border-cyan-400/80 shadow-2xl shadow-cyan-500/30" data-testid="sign-on-priority-card">

      <CardContent className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="p-4 bg-cyan-500/20 rounded-2xl animate-pulse">
            <PenLine className="w-10 h-10 text-cyan-300" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white tracking-tight">SIGN ON TO START SHIFT</h2>
            <p className="text-sm text-cyan-200/80 mt-0.5">
              {shift.workflow_stage === 'accepted' && 'Complete all sign-on steps before your shift can begin'}
              {shift.workflow_stage === 'sop_acknowledged' && 'SOP acknowledged — continue sign-on'}
              {shift.workflow_stage === 'fit_for_work' && 'Fitness confirmed — complete remaining steps'}
              {shift.workflow_stage === 'geofence_validated' && 'Location verified — provide your signature'}
              {shift.workflow_stage === 'signed_on' && 'Signed on — start your shift now'}
            </p>
          </div>
        </div>

        {/* ── COUNTDOWN TIMER ────────────────────────────────────────── */}
        {countdown.text && (
          <div
            className={`flex items-center justify-between rounded-lg px-4 py-2.5 border ${uStyle.bg} ${uStyle.border} transition-colors`}
            data-testid="shift-countdown"
            data-urgency={countdown.urgency}
          >
            <div className="flex items-center gap-2">
              <Clock className={`w-4 h-4 ${uStyle.icon} ${countdown.urgency === 'critical' ? 'animate-pulse' : ''}`} />
              <span className={`text-sm font-semibold ${uStyle.text}`} data-testid="countdown-text">
                {countdown.text}
              </span>
            </div>
            {countdown.urgency === 'critical' && (
              <Badge className="bg-red-500/20 text-red-300 border-red-500/50 text-[10px] px-2 py-0.5" data-testid="overdue-badge">
                OVERDUE
              </Badge>
            )}
            {countdown.urgency === 'warning' && (
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/50 text-[10px] px-2 py-0.5" data-testid="soon-badge">
                STARTING SOON
              </Badge>
            )}
          </div>
        )}

        {/* Site + Time */}
        <div className="bg-[#09090b]/60 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-white font-semibold text-lg">
            <MapPin className="w-5 h-5 text-cyan-400 flex-shrink-0" />
            {
shift.display_name ||
shift.deployment_name ||
shift.patrol_plan_name ||
shift.deployment_site_name ||
shift.site_name ||
'Assigned Site'
}
          </div>
          <div className="flex items-center gap-4 text-sm text-zinc-300">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-cyan-400/70" />
              {fmtDate(shift.start_time)}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-cyan-400/70" />
              {fmt(shift.start_time)} – {fmt(shift.end_time)}
            </span>
          </div>
          {shift.shift_type === 'mobile_patrol' && (
            <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40 mt-1">Mobile Patrol</Badge>
          )}
        </div>

        {/* Progress bar */}
        <div className="grid grid-cols-5 gap-1">
          {stageOrder.map((stage, idx) => {
            const isDone = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            return (
              <div key={stage} className="text-center">
                <div className={`h-2 rounded-full mb-1 transition-colors ${isDone ? 'bg-cyan-400' : isCurrent ? 'bg-cyan-500/60 animate-pulse' : 'bg-zinc-700'}`} />
                <span className={`text-[10px] font-medium ${isDone ? 'text-cyan-400' : isCurrent ? 'text-white' : 'text-zinc-600'}`}>
                  {stageLabels[stage]}
                </span>
              </div>
            );
          })}
        </div>

        {/* CTA — always dominant */}
        <Button
          className="w-full h-16 text-xl font-bold bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-white rounded-xl shadow-xl shadow-cyan-500/40 transition-all active:scale-[0.98]"
          onClick={onContinue}
          disabled={loading}
          data-testid="sign-on-primary-btn"
        >
          {loading ? <Loader2 className="w-6 h-6 mr-3 animate-spin" /> : <PenLine className="w-7 h-7 mr-3" />}
          {shift.workflow_stage === 'signed_on' ? 'START SHIFT' : 'CONTINUE SIGN-ON'}
        </Button>

        {(shift.notes || shift.instructions) && (
          <div className="bg-amber-500/5 p-3 rounded-lg border border-amber-500/30" data-testid="shift-instructions-panel">
            <p className="text-[10px] text-amber-400 font-bold uppercase mb-1 tracking-wider">
              Guard Instructions
            </p>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">
              {shift.notes || shift.instructions}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ACTIVE SHIFT PANEL — Primary operational container with embedded patrol
// ═══════════════════════════════════════════════════════════════════════════
function ActiveShiftPanelInner({ shift, patrol, onSignOff, onStartPatrol, onResumePatrol, onPanicAlert, isTracking, trackingError, pingCount, lastPing }) {
  const stage = (shift.workflow_stage || '').toLowerCase();
  const hasPatrol = !!patrol;
  const patrolActive = hasPatrol && patrol.status === 'in_progress';
  const patrolAssigned = hasPatrol && patrol.status === 'assigned';
  const allCheckpointsDone = hasPatrol && patrol.total_stops > 0 && patrol.completed_stops >= patrol.total_stops;

  // FIX: prevent runtime crash after shift start
  const canResumePatrol =
    hasPatrol &&
    (
      patrol.status === 'in_progress' ||
      patrol.status === 'paused' ||
      patrol.status === 'active'
    );

  const isSectorPatrolSite =
    shift?.site_type === 'static' &&
    shift?.uses_sector_patrols === true;

  const isStandardStaticSite =
    shift?.site_type === 'static' &&
    !shift?.uses_sector_patrols;

  // Determine action required message
  let actionMsg = null;
  let actionColor = 'text-amber-400';
  if (stage === 'started' && patrolAssigned) {
    actionMsg = 'ACTION REQUIRED: Start your patrol';
    actionColor = 'text-amber-400';
  } else if (patrolActive && !allCheckpointsDone) {
    actionMsg = 'ACTION REQUIRED: Patrol must be completed';
    actionColor = 'text-amber-400';
  } else if (allCheckpointsDone) {
    actionMsg = 'Patrol complete — you may sign off';
    actionColor = 'text-emerald-400';
  }

  return (
    <Card className="bg-gradient-to-br from-[#18181b] to-[#1a1a2e] border-2 border-blue-500/60 shadow-lg" data-testid="active-shift-panel">
      <CardContent className="p-5 space-y-4">
        {/* Action Required Banner */}
        {actionMsg && (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${actionColor === 'text-emerald-400' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
            <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${actionColor}`} />
            <span className={`text-sm font-bold ${actionColor}`}>{actionMsg}</span>
          </div>
        )}

        {/* Shift header */}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">{
shift.display_name ||
shift.deployment_name ||
shift.patrol_plan_name ||
shift.deployment_site_name ||
shift.site_name ||
'Active Shift'
}</h3>
            <p className="text-sm text-zinc-400">
              {(() => {
                const shiftStart = shift?.start_time || shift?.scheduled_start;
                const shiftEnd = shift?.end_time || shift?.scheduled_end;

                const startMs = shiftStart ? new Date(shiftStart).getTime() : null;
                const endMs = shiftEnd ? new Date(shiftEnd).getTime() : null;

                const invalidRange =
                  Number.isFinite(startMs) &&
                  Number.isFinite(endMs) &&
                  endMs < startMs;

                if (invalidRange) {
                  console.error('[SHIFT TIME ERROR]', {
                    shiftId: shift?._id,
                    shiftStart,
                    shiftEnd,
                    shift
                  });

                  return 'Invalid Shift Time';
                }

                return `${fmt(shiftStart)} – ${fmt(shiftEnd)}`;
              })()}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Badge className={
              stage === 'patrol_active' ? 'bg-green-500/20 text-green-400 border-green-500/40' :
              'bg-blue-500/20 text-blue-400 border-blue-500/40'
            } data-testid="shift-stage-badge">
              {stage === 'patrol_active' ? (isSectorPatrolSite ? 'Sector Checks Active' : 'Patrol Active') :
               stage === 'started' ? 'Shift Started' :
               stage.replace(/_/g, ' ')}
            </Badge>
            {isSectorPatrolSite && (
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40">
                SECTOR PATROL SITE
              </Badge>
            )}
          </div>
        </div>

        {/* GPS tracking indicator */}
        {isTracking && (
          <div className="flex items-center gap-3 text-xs text-zinc-500 bg-[#09090b] rounded-lg p-2">
            <div className="flex items-center gap-1">
              <Navigation className="w-3 h-3 text-emerald-400" />
              <span className="text-emerald-400 font-medium">LIVE</span>
            </div>
            <span>tracking active</span>
            {lastPing?.response?.battery_level && (
              <div className="flex items-center gap-1">
                <Battery className={`w-3 h-3 ${lastPing.response.battery_level < 20 ? 'text-red-400' : 'text-zinc-400'}`} />
                <span>{lastPing.response.battery_level}%</span>
              </div>
            )}
          </div>
        )}
        {trackingError && (
          <div className="text-xs text-amber-400 bg-amber-500/10 rounded p-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {trackingError}
          </div>
        )}

        {(shift.notes || shift.instructions) && (
          <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="active-shift-instructions">
            <p className="text-[10px] text-amber-400 font-bold uppercase mb-1 tracking-wider">
              Guard Instructions
            </p>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">
              {shift.notes || shift.instructions}
            </p>
          </div>
        )}

        {/* ── EMBEDDED PATROL ──────────────────────────────────────────── */}
        {hasPatrol && (
          <div className="bg-[#09090b]/80 rounded-xl p-4 space-y-3 border border-zinc-800" data-testid="embedded-patrol-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Route className="w-5 h-5 text-blue-400" />
                <span className="text-white font-semibold">{patrol.route_name || 'Patrol Route'}</span>
              </div>
              <Badge className={
                patrolActive ? 'bg-emerald-500/20 text-emerald-400' :
                allCheckpointsDone ? 'bg-green-500/20 text-green-400' :
                'bg-zinc-500/20 text-zinc-400'
              }>
                {allCheckpointsDone ? 'Complete' : patrolActive ? 'In Progress' : 'Assigned'}
              </Badge>
            </div>

            {/* Progress bar */}
            {patrol.total_stops > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-zinc-400">
                  <span>{patrol.completed_stops}/{patrol.total_stops} checkpoints</span>
                  <span>{patrol.progress_pct}%</span>
                </div>
                <div className="h-2.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500 rounded-full"
                    style={{ width: `${patrol.progress_pct}%` }}
                    data-testid="patrol-progress-bar"
                  />
                </div>
              </div>
            )}

            {/* Checkpoint list */}
            {patrol.stops && patrol.stops.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {patrol.stops.sort((a, b) => (a.order || 0) - (b.order || 0)).map((stop, i) => (
                  <div key={stop.id || i} className="flex items-center gap-2 text-sm py-1">
                    {stop.status === 'completed' || stop.status === 'checked_in' ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-zinc-600 flex-shrink-0" />
                    )}
                    <span className={stop.status === 'completed' || stop.status === 'checked_in' ? 'text-zinc-400 line-through' : 'text-white'}>
                      {stop.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* ── NEXT ACTION ENGINE ──────────────────────────────────────── */}
        {/* signed_on/started + patrol assigned → START PATROL */}
        {((stage === 'started' || stage === 'signed_on') || (patrolActive && !canResumePatrol)) && patrolAssigned && (
          <Button
            className="w-full h-14 text-lg font-bold bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-xl shadow-lg shadow-blue-500/30 transition-all active:scale-[0.98]"
            onClick={onStartPatrol}
            data-testid="start-patrol-btn"
          >
            <Play className="w-6 h-6 mr-3" />
            {isSectorPatrolSite
              ? 'START SECTOR CHECKS'
              : 'START PATROL'}
          </Button>
        )}

        {/* patrol_active → RESUME PATROL */}
        {canResumePatrol && (
          <Button
            className="w-full h-14 text-lg font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white rounded-xl shadow-lg shadow-emerald-500/30 transition-all active:scale-[0.98]"
            onClick={onResumePatrol}
            data-testid="resume-patrol-btn"
          >
            <Navigation className="w-6 h-6 mr-3" />
            {isSectorPatrolSite
              ? 'RESUME SECTOR CHECKS'
              : 'RESUME PATROL'}
          </Button>
        )}

        {/* completed / no patrol → SIGN OFF */}
        {(allCheckpointsDone || !hasPatrol || (hasPatrol && patrol.total_stops === 0)) && (
          <Button
            className="w-full h-14 text-lg font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white rounded-xl shadow-lg shadow-emerald-500/30 transition-all active:scale-[0.98]"
            onClick={onSignOff}
            data-testid="sign-off-shift-btn"
          >
            <ClipboardCheck className="w-6 h-6 mr-3" />
            SIGN OFF SHIFT
          </Button>
        )}

        <Button
          className="w-full h-14 text-lg font-black bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white rounded-xl shadow-lg shadow-red-900/50 border border-red-400/30 active:scale-[0.98]"
          onClick={() => onPanicAlert?.(shift)}
          data-testid="panic-alert-btn"
        >
          🚨 PANIC ALERT
        </Button>
      </CardContent>
    </Card>
  );
}

const ActiveShiftPanel = React.memo(ActiveShiftPanelInner, (prev, next) => {
  const prevShift = prev.shift || {};
  const nextShift = next.shift || {};
  const prevPatrol = prev.patrol || {};
  const nextPatrol = next.patrol || {};

  return (
    prevShift.id === nextShift.id &&
    prevShift._id === nextShift._id &&
    prevShift.workflow_stage === nextShift.workflow_stage &&
    prevShift.status === nextShift.status &&
    prevShift.site_name === nextShift.site_name &&
    prevShift.start_time === nextShift.start_time &&
    prevShift.end_time === nextShift.end_time &&
    prevPatrol.id === nextPatrol.id &&
    prevPatrol._id === nextPatrol._id &&
    prevPatrol.status === nextPatrol.status &&
    prevPatrol.completed_stops === nextPatrol.completed_stops &&
    prevPatrol.total_stops === nextPatrol.total_stops &&
    prevPatrol.progress_pct === nextPatrol.progress_pct
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════
export default function OfficerDashboard() {
  const { api, user } = useAuth();
  const navigate = useNavigate();

  const { isTracking, lastPing, lastError: trackingError, pingCount, startTracking } = useLocationTracking();

  const [loading, setLoading] = useState(true);
  const [masterLicences, setMasterLicences] = useState([]);
  const [data, setData] = useState({
    pending_acceptance: [],
    accepted_pending_signon: [],
    active_shifts: [],
    assigned_patrol: null,
  });
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [actionLoading, setActionLoading] = useState(null);

  // RAPID X V6.8 — Officer emergency state
  const [activeEmergency, setActiveEmergency] = useState(null);

  // Modals
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [selectedWorkflowShift, setSelectedWorkflowShift] = useState(null);

  // RAPID X V6.4.5B — POLL STABILITY
  const workflowModalOpenRef = useRef(false);
  const selectedWorkflowShiftRef = useRef(null);

  useEffect(() => {
    workflowModalOpenRef.current =
      workflowModalOpen;
  }, [workflowModalOpen]);

  useEffect(() => {
    selectedWorkflowShiftRef.current =
      selectedWorkflowShift;
  }, [selectedWorkflowShift]);

  const [signOffModalOpen, setSignOffModalOpen] = useState(false);


  const [selectedSignOffShift, setSelectedSignOffShift] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [shiftToReject, setShiftToReject] = useState(null);

  // New shift alerts
  const [newShiftAlerts, setNewShiftAlerts] = useState([]);
  const [lastKnownIds, setLastKnownIds] = useState(new Set());

  // ─── FETCH — Single endpoint, single source of truth ──────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);


      // LOAD MASTER LICENCES
      try {
        const mlRes = await api.get('/personnel-compliance/master-licences/officer-dashboard');

        setMasterLicences(
          mlRes?.data?.master_licences || []
        );
      } catch (mlErr) {
        console.error('MASTER LICENCE LOAD ERROR:', mlErr);
      }


      const res = await api.get('/dashboard/officer', {
        headers: { 'Cache-Control': 'no-cache, no-store' }
      });
      const d = res.data || {};

      // Detect new shifts for alert
      const allIds = new Set([
        ...(d.pending_acceptance || []).map(s => s.id),
        ...(d.accepted_pending_signon || []).map(s => s.id),
        ...(d.active_shifts || []).map(s => s.id),
      ]);
      if (lastKnownIds.size > 0) {
        const newOnes = (d.pending_acceptance || []).filter(s => !lastKnownIds.has(s.id));
        if (newOnes.length > 0) setNewShiftAlerts(newOnes);
      }
      setLastKnownIds(allIds);

      const incomingDashboard = {
        pending_acceptance:
          d.pending_acceptance || [],
        accepted_pending_signon:
          d.accepted_pending_signon || [],
        active_shifts:
          d.active_shifts || [],
        assigned_patrol:
          d.assigned_activePatrol || null,
      };

      setData((previousDashboard) => {
        const incomingIsEmpty =
          incomingDashboard.pending_acceptance.length === 0 &&
          incomingDashboard.accepted_pending_signon.length === 0 &&
          incomingDashboard.active_shifts.length === 0;

        const previousHasShift =
          previousDashboard.pending_acceptance.length > 0 ||
          previousDashboard.accepted_pending_signon.length > 0 ||
          previousDashboard.active_shifts.length > 0;

        if (
          workflowModalOpenRef.current &&
          selectedWorkflowShiftRef.current &&
          incomingIsEmpty &&
          previousHasShift
        ) {
          console.warn(
            '[Rapid X V6.4.5B] Ignored transient empty dashboard poll'
          );

          return previousDashboard;
        }

        return incomingDashboard;
      });
      setLastUpdated(new Date());
    } catch (err) {
      console.error('[OfficerDashboard] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [api, lastKnownIds]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 60000);
    return () => clearInterval(iv);

  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // RAPID X V6.8 — Keep officer informed of emergency status.
  // Backend remains the source of truth, so this survives refresh,
  // navigation and temporary websocket/network interruption.
  const loadOfficerEmergency = useCallback(async () => {
    try {
      const currentShift =
        data.active_shifts?.[0] || null;

      const shiftId =
        currentShift?._id ||
        currentShift?.id ||
        null;

      const response = await api.get(
        '/panic/mine',
        {
          params: shiftId
            ? { shift_id: shiftId }
            : {}
        }
      );

      setActiveEmergency(
        response?.data?.alert || null
      );
    } catch (error) {
      console.error(
        '[OfficerDashboard] emergency status load failed:',
        error
      );
    }
  }, [api, user, data.active_shifts]);

  useEffect(() => {
    loadOfficerEmergency();

    const emergencyPoll = setInterval(
      loadOfficerEmergency,
      5000
    );

    return () => clearInterval(emergencyPoll);
  }, [loadOfficerEmergency]);

  // Auto-start GPS when active shift
  useEffect(() => {
    const active = data.active_shifts[0];
    if (active && ACTIVE_WORKFLOW_STAGES.includes(active.workflow_stage) && !isTracking) {
      startTracking();
    }
  }, [data.active_shifts, isTracking, startTracking]);

  // ─── ACTIONS ──────────────────────────────────────────────────────────
  const handleAcceptShift = async (shift) => {
    const shiftId = shift?.id || shift?._id;

    if (!shiftId) {
      toast.error('No shift selected');
      return;
    }

    setActionLoading(shiftId);

    try {
      try {
        await api.post(
          `/shifts/${shiftId}/respond`,
          { response: 'accepted' }
        );
      } catch (acceptError) {
        const detail =
          acceptError?.response?.data?.detail;

        const alreadyAccepted =
          typeof detail === 'string' &&
          (
            detail.toLowerCase().includes(
              'already accepted'
            ) ||
            detail.toLowerCase().includes(
              'current stage'
            )
          );

        if (!alreadyAccepted) {
          console.error(
            '[OfficerDashboard] Accept shift failed:',
            acceptError
          );

          toast.error(
            typeof detail === 'string'
              ? detail
              : 'Failed to accept shift'
          );

          return;
        }
      }

      const acceptedShift = {
        ...shift,
        id: shiftId,
        acceptance_status: 'accepted',
        status: 'accepted',
        workflow_stage: 'accepted',
        accepted_at:
          shift?.accepted_at ||
          new Date().toISOString(),
      };

      setData((previous) => ({
        ...previous,
        pending_acceptance: (
          previous.pending_acceptance || []
        ).filter(
          (item) =>
            (item.id || item._id) !== shiftId
        ),
        accepted_pending_signon: [
          acceptedShift,
          ...(
            previous.accepted_pending_signon || []
          ).filter(
            (item) =>
              (item.id || item._id) !== shiftId
          ),
        ],
      }));

      toast.success('Shift accepted');

      try {
        await fetchData();
      } catch (refreshError) {
        console.warn(
          '[OfficerDashboard] Acceptance succeeded but refresh failed:',
          refreshError
        );
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectShift = async () => {
    if (!shiftToReject || rejectReason.trim().length < 5) {
      toast.error('Rejection reason must be at least 5 characters');
      return;
    }
    try {
      setActionLoading(shiftToReject.id);
      await api.post(`/shifts/${shiftToReject.id}/reject`, { reason: rejectReason.trim() });
      toast.success('Shift rejected');
      setShowRejectModal(false);
      setRejectReason('');
      setShiftToReject(null);
      await fetchData();
    } catch (err) {
      toast.error(extractApiError(err) || 'Failed to reject shift');
    } finally {
      setActionLoading(null);
    }
  };

  const handleContinueWorkflow = (shift) => {
    setSelectedWorkflowShift(shift);
    setWorkflowModalOpen(true);
  };

  const handleWorkflowComplete = (completedShift) => {
    setWorkflowModalOpen(false);
    setSelectedWorkflowShift(null);
    fetchData();
    toast.success('Workflow step completed');

    // Auto-navigate to patrol for mobile patrol after sign-on
    if (completedShift?.shift_type === 'mobile_patrol' && completedShift?.assigned_activePatrol?.route_run_id) {
      setTimeout(() => navigate('/my-routes'), 800);
    }
  };

  const getPatrolNavigationPath = (shift) => {
    const patrolPlanId =
      shift?.patrol_plan_id ||
      (
        shift?.deployment_type === 'patrol_plan'
          ? shift?.deployment_id
          : null
      );

    const shiftId = shift?.id || shift?._id;

    if (patrolPlanId) {
      return `/my-routes?patrol_plan_id=${encodeURIComponent(patrolPlanId)}${shiftId ? `&shift_id=${encodeURIComponent(shiftId)}` : ''}`;
    }

    return '/my-routes';
  };

  const handleStartPatrol = (shift = currentActiveShift) => {
    navigate(getPatrolNavigationPath(shift));
  };

  const handleResumePatrol = (shift = currentActiveShift) => {
    navigate(getPatrolNavigationPath(shift));
  };

  const getEmergencyPosition = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.warn(
            '[PANIC GPS] Fresh GPS unavailable; panic will still be sent',
            error
          );
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    });


  const handlePanicAlert = async (shift) => {
    const confirmed = window.confirm(
      "Trigger PANIC ALERT? This will immediately notify Command Centre."
    );

    if (!confirmed) return;

    try {
      const emergencyPosition = await getEmergencyPosition();

      const response = await api.post('/panic', {
        shift_id: shift?._id || shift?.id,
        site_id: shift?.site_id,
        site_name: shift?.site_name,
        latitude: emergencyPosition?.latitude ?? null,
        longitude: emergencyPosition?.longitude ?? null,
        accuracy: emergencyPosition?.accuracy ?? null,
        message: emergencyPosition
          ? 'Officer initiated panic alert'
          : 'Officer initiated panic alert — fresh GPS unavailable',
      });

      const panicResult = response?.data || {};

      // Immediately refresh canonical backend emergency state.
      await loadOfficerEmergency();

      if (panicResult.already_active) {
        alert(
          `EMERGENCY ALREADY ACTIVE\n\n` +
          `Command Centre has already been notified.\n` +
          `Reference: ${panicResult.alert_id || 'Available in dashboard'}`
        );
      } else {
        alert(
          emergencyPosition
            ? (
                `PANIC ALERT SENT\n\n` +
                `Command Centre has been notified and has your location.\n` +
                `Reference: ${panicResult.alert_id || 'Pending'}`
              )
            : (
                `PANIC ALERT SENT\n\n` +
                `Command Centre has been notified.\n` +
                `Fresh GPS was unavailable.\n` +
                `Reference: ${panicResult.alert_id || 'Pending'}`
              )
        );
      }
    } catch (err) {
      console.error('[PANIC ALERT ERROR]', err);
      alert('Failed to send panic alert');
    }
  };

  const handleSignOff = (shift) => {
    setSelectedSignOffShift(shift);
    setSignOffModalOpen(true);
  };

  // ─── DERIVED STATE ────────────────────────────────────────────────────

  const { pending_acceptance, accepted_pending_signon, active_shifts, assigned_patrol } = data;
  const hasSignOnPending = accepted_pending_signon.length > 0;
  const hasActiveShift = active_shifts.length > 0;

  // STATIC SHIFT DETECTION
  const currentActiveShift = active_shifts?.[0] || null;
  const activeShiftRouteIds = [
    ...(Array.isArray(currentActiveShift?.patrol_route_ids) ? currentActiveShift.patrol_route_ids : []),
    currentActiveShift?.assigned_route_id,
    currentActiveShift?.route_id,
    currentActiveShift?.patrol_route_id,
  ].filter(Boolean);

  const isMobilePatrolShift = currentActiveShift?.shift_type === 'mobile_patrol';

  const isStaticShift =
    currentActiveShift &&
    !isMobilePatrolShift &&
    activeShiftRouteIds.length === 0;

  const hasPending = pending_acceptance.length > 0;
  const primaryActive = active_shifts[0] || null;

  // ─── LOADING ──────────────────────────────────────────────────────────
  if (loading && !lastKnownIds.size) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <>
      <div className="mb-6">
      </div>

      <div className="p-4 md:p-6 space-y-4 max-w-4xl mx-auto" data-testid="officer-dashboard">
      {/* New shift alert banner */}
      <NewShiftAlert shifts={newShiftAlerts} onDismiss={() => setNewShiftAlerts([])} />

      {/* Header — always visible */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-rajdhani font-bold text-white">OFFICER DASHBOARD</h1>




          <p className="text-xs text-zinc-500">
            Last updated: {lastUpdated.toLocaleTimeString('en-AU', {
              timeZone: 'Australia/Sydney',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="border-zinc-600" data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          SIGN-ON DOMINANCE — If pending sign-on, HIDE EVERYTHING ELSE
          ══════════════════════════════════════════════════════════════════ */}
      {hasSignOnPending ? (
        <>
          <SignOnPriorityCard
            shift={accepted_pending_signon[0]}
            onContinue={() => handleContinueWorkflow(accepted_pending_signon[0])}
            loading={!!actionLoading}
          />
          {accepted_pending_signon.length > 1 && (
            <p className="text-xs text-cyan-300/60 text-center">
              +{accepted_pending_signon.length - 1} more shift(s) awaiting sign-on
            </p>
          )}
        </>
      ) : (
        <>
          {/* ════════════════════════════════════════════════════════════
              PENDING ACCEPTANCE — Hero card
              ════════════════════════════════════════════════════════════ */}
          {hasPending && !hasActiveShift && (
            <Card className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 border-amber-500/50 shadow-lg shadow-amber-500/20" data-testid="pending-acceptance-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-amber-500/20 rounded-lg animate-bounce">
                      <Bell className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-white font-bold">YOUR NEXT ASSIGNED SHIFT</CardTitle>
                      <p className="text-sm text-amber-300/70">ACTION REQUIRED — Accept or Reject</p>
                    </div>
                  </div>
                  <Badge className="bg-amber-500/30 text-amber-300 border-2 border-amber-500 animate-pulse">PENDING</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#09090b]/70 rounded-xl border-2 border-amber-500/30">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-7 h-7 text-amber-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-2xl font-bold text-white">{pending_acceptance[0].display_name ||
pending_acceptance[0].deployment_name ||
pending_acceptance[0].patrol_plan_name ||
pending_acceptance[0].deployment_site_name ||
pending_acceptance[0].site_name ||
'Assigned Site'}</h3>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-amber-400" />
                          <span className="text-white">{fmtDate(pending_acceptance[0].start_time)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-amber-400" />
                          <span className="text-white">{fmt(pending_acceptance[0].start_time)} – {fmt(pending_acceptance[0].end_time)}</span>
                        </div>
                      </div>
                      {(pending_acceptance[0].notes || pending_acceptance[0].instructions) && (
                        <div className="mt-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5" data-testid="next-shift-instructions">
                          <p className="text-[10px] text-amber-400 font-bold uppercase mb-1 tracking-wider">Guard Instructions</p>
                          <p className="text-sm text-zinc-200 whitespace-pre-wrap">
                            {pending_acceptance[0].notes || pending_acceptance[0].instructions}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    className="h-16 text-lg font-bold bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 active:scale-95 transition-all"
                    onClick={() => handleAcceptShift(pending_acceptance[0])}
                    disabled={actionLoading === pending_acceptance[0].id}
                    data-testid="accept-shift-btn"
                  >
                    {actionLoading === pending_acceptance[0].id ? <Loader2 className="w-6 h-6 mr-2 animate-spin" /> : <Check className="w-6 h-6 mr-2" />}
                    ACCEPT
                  </Button>
                  <Button
                    className="h-16 text-lg font-bold bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/30 active:scale-95 transition-all"
                    onClick={() => { setShiftToReject(pending_acceptance[0]); setShowRejectModal(true); }}
                    disabled={actionLoading === pending_acceptance[0].id}
                    data-testid="reject-shift-btn"
                  >
                    <X className="w-6 h-6 mr-2" />
                    REJECT
                  </Button>
                </div>
                {pending_acceptance.length > 1 && (
                  <p className="text-sm text-amber-300 text-center">+{pending_acceptance.length - 1} more shift(s) awaiting response</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* All pending shifts list (when active shift exists) */}
          {hasPending && hasActiveShift && (
            <Card className="bg-[#18181b] border-amber-500/50 border-2" data-testid="pending-shifts-list">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
                  <Bell className="w-5 h-5 animate-bounce" />
                  SHIFTS AWAITING RESPONSE ({pending_acceptance.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {pending_acceptance.map(shift => (
                  <div key={shift.id} className="p-4 bg-[#09090b] rounded-xl border border-amber-500/30" data-testid={`pending-shift-${shift.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-white font-semibold">{
shift.display_name ||
shift.deployment_name ||
shift.patrol_plan_name ||
shift.deployment_site_name ||
shift.site_name
}</p>
                        <p className="text-xs text-zinc-400">{fmtDate(shift.start_time)} • {fmt(shift.start_time)} – {fmt(shift.end_time)}</p>
                      </div>
                      <Badge className="bg-amber-500/20 text-amber-400">Pending</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button className="bg-emerald-600 hover:bg-emerald-500 text-white h-12 font-bold" onClick={() => handleAcceptShift(shift)} disabled={actionLoading === shift.id} data-testid={`accept-shift-${shift.id}`}>
                        {actionLoading === shift.id ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />} ACCEPT
                      </Button>
                      <Button className="bg-red-600 hover:bg-red-500 text-white h-12 font-bold" onClick={() => { setShiftToReject(shift); setShowRejectModal(true); }} disabled={actionLoading === shift.id} data-testid={`decline-shift-${shift.id}`}>
                        <X className="w-5 h-5 mr-2" /> REJECT
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* RAPID X V6.8 — Persistent officer emergency status */}
          {activeEmergency && (
            <Card
              className={
                activeEmergency.status === 'acknowledged'
                  ? 'bg-amber-950/80 border-2 border-amber-400 shadow-xl shadow-amber-500/20'
                  : 'bg-red-950/80 border-2 border-red-500 shadow-xl shadow-red-500/30'
              }
              data-testid="officer-emergency-status"
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div
                    className={
                      activeEmergency.status === 'acknowledged'
                        ? 'p-3 rounded-xl bg-amber-500/20'
                        : 'p-3 rounded-xl bg-red-500/20 animate-pulse'
                    }
                  >
                    <AlertTriangle
                      className={
                        activeEmergency.status === 'acknowledged'
                          ? 'w-8 h-8 text-amber-300'
                          : 'w-8 h-8 text-red-300'
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-black text-white">
                          {activeEmergency.status === 'responding'
                            ? 'CONTROL ROOM RESPONDING'
                            : activeEmergency.status === 'acknowledged'
                            ? 'COMMAND CENTRE ACKNOWLEDGED'
                            : 'EMERGENCY ALERT ACTIVE'}
                      </h2>

                      <Badge
                        className={
                            activeEmergency.status === 'responding'
                              ? 'bg-yellow-500/20 text-yellow-200 border-yellow-400/50'
                              : activeEmergency.status === 'acknowledged'
                              ? 'bg-amber-500/20 text-amber-200 border-amber-400/50'
                              : 'bg-red-500/20 text-red-200 border-red-400/50'
                        }
                      >
                        {String(
                          activeEmergency.status || 'active'
                        ).toUpperCase()}
                      </Badge>
                    </div>

                    <p className="mt-2 text-sm text-zinc-200">
                        {activeEmergency.status === 'responding'
                          ? (
                              'Control Room is actively responding to your emergency. ' +
                              'Your emergency remains active until Control Room formally resolves the response.'
                            )
                          : activeEmergency.status === 'acknowledged'
                          ? (
                              'Command Centre has received and acknowledged your emergency alert. ' +
                              'Remain available and follow Control Room instructions.'
                            )
                          : (
                              'Command Centre has been notified. ' +
                              'Your emergency remains active until Control Room acknowledges and completes the response.'
                            )}
                    </p>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      {activeEmergency.site_name && (
                        <div className="rounded-lg bg-black/30 px-3 py-2">
                          <span className="text-zinc-400">Site</span>
                          <div className="font-semibold text-white">
                            {activeEmergency.site_name}
                          </div>
                        </div>
                      )}

                      {activeEmergency.acknowledged_by_name && (
                        <div className="rounded-lg bg-black/30 px-3 py-2">
                          <span className="text-zinc-400">
                            Acknowledged by
                          </span>
                          <div className="font-semibold text-white">
                            {activeEmergency.acknowledged_by_name}
                          </div>
                        </div>
                      )}

                      {activeEmergency.acknowledged_at && (
                        <div className="rounded-lg bg-black/30 px-3 py-2">
                          <span className="text-zinc-400">
                            Acknowledged at
                          </span>
                          <div className="font-semibold text-white">
                            {fmt(activeEmergency.acknowledged_at)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ════════════════════════════════════════════════════════════
              ACTIVE SHIFT — Primary operational container
              ════════════════════════════════════════════════════════════ */}
          {hasActiveShift && (
            <ActiveShiftPanel
              shift={primaryActive}
              patrol={primaryActive.activePatrol || assigned_patrol}
              onSignOff={() => handleSignOff(primaryActive)}
              onStartPatrol={handleStartPatrol}
              onResumePatrol={handleResumePatrol}
              onPanicAlert={handlePanicAlert}
              isTracking={isTracking}
              trackingError={trackingError}
              pingCount={0}
              lastPing={null}
            />
          )}

          {/* Additional active shifts */}
          {active_shifts.length > 1 && active_shifts.slice(1).map((shift, idx) => (
            <ActiveShiftPanel
              key={shift.id}
              shift={shift}
              patrol={shift.activePatrol || null}
              onSignOff={() => handleSignOff(shift)}
              onStartPatrol={handleStartPatrol}
              onResumePatrol={handleResumePatrol}
              onPanicAlert={handlePanicAlert}
              isTracking={false}
              trackingError={null}
              pingCount={0}
              lastPing={null}
            />
          ))}

          {/* ════════════════════════════════════════════════════════════
              EMPTY STATE
              ════════════════════════════════════════════════════════════ */}
          {!hasPending && !hasActiveShift && (
            <Card className="bg-[#18181b] border-[#3f3f46]" data-testid="no-shifts-card">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-zinc-400">No Shifts Allocated</h3>
                <p className="text-sm text-zinc-500 mt-1">You don't have any active or upcoming shifts.</p>
                <p className="text-xs text-zinc-600 mt-2">Contact your supervisor if you believe this is an error.</p>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions — only when no sign-on dominance */}
          {(hasActiveShift || hasPending) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {hasActiveShift && (
                <Button className="h-12 bg-blue-600 hover:bg-blue-500" onClick={() => handleResumePatrol(currentActiveShift)} data-testid="quick-patrol-btn">
                  <Route className="w-5 h-5 mr-2" /> Continue Patrol
                </Button>
              )}
              <Button className="h-12 bg-amber-600 hover:bg-amber-500 text-black font-semibold" onClick={() => navigate('/incidents')} data-testid="quick-incident-btn">
                <FileWarning className="w-5 h-5 mr-2" /> Report Incident
              </Button>

              <Button
                className="h-12 bg-zinc-700 hover:bg-zinc-600 text-white"
                onClick={() => navigate('/activity-log')}
                data-testid="quick-activity-log-btn"
              >
                Activity Log
              </Button>
            </div>
          )}
        </>
      )}

      {/* ═══════ MODALS ═══════════════════════════════════════════════════ */}
      <div className="mt-4">
        <WeatherForecastWidget />
      </div>

      <ShiftWorkflowModal
        isOpen={workflowModalOpen}
        onClose={() => { setWorkflowModalOpen(false); setSelectedWorkflowShift(null); }}
        shift={selectedWorkflowShift}
        api={api}
        onWorkflowComplete={handleWorkflowComplete}
      />
      <ShiftSignOffModal
        isOpen={signOffModalOpen}
        onClose={() => { setSignOffModalOpen(false); setSelectedSignOffShift(null); }}
        shift={selectedSignOffShift}
        api={api}
        onSignOffComplete={() => { fetchData(); toast.success('Shift signed off successfully'); }}
      />

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <X className="w-5 h-5" /> Reject Shift
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Provide a reason for rejection. Your supervisor will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {shiftToReject && (
              <div className="p-3 bg-[#09090b] rounded-lg border border-zinc-800">
                <p className="font-semibold text-white">{getShiftDisplayName(shiftToReject)}</p>
                <p className="text-sm text-zinc-400">{fmt(shiftToReject.start_time)} – {fmt(shiftToReject.end_time)}</p>
              </div>
            )}
            <Textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (min 5 characters)..."
              className="bg-[#09090b] border-[#3f3f46] min-h-[100px]"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectReason(''); setShiftToReject(null); }} className="border-zinc-600">Cancel</Button>
            <Button onClick={handleRejectShift} className="bg-red-600 hover:bg-red-700" disabled={rejectReason.trim().length < 5 || actionLoading === shiftToReject?.id} data-testid="confirm-reject-btn">
              {actionLoading === shiftToReject?.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
              Reject Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
}
